import {
  ChannelKind,
  IChannelListItem,
  IChannelTypingUser,
  IConversationGroup,
} from '@common/interfaces/channels'
import isEqual from 'lodash/isEqual'
import { createSelector } from 'reselect'
import { assignGroupEmojiDefaults, resolveGroupEmojis } from '../../helpers/groupEmoji'
import { IRootState } from '../reducer'
import { prefix } from './actions'
import { personOfPane } from './splitPanes'
import {
  UnreadIndicator,
  aggregateConversationsUnread,
  unreadIndicatorValue,
} from './unreadIndicator'

export const getChannelsState = (state: IRootState) => state[prefix]

export const channelsList = (state: IRootState) => state[prefix].channels
export const isLoadingChannels = (state: IRootState) => state[prefix].isLoadingChannels
export const activeChannelId = (state: IRootState) => state[prefix].activeChannelId
export const activeChannel = (state: IRootState) => state[prefix].activeChannel
export const settingsChannel = (state: IRootState) => state[prefix].settingsChannel
export const channelMessages = (state: IRootState) => state[prefix].messages
export const browseChannelsData = (state: IRootState) => state[prefix].browse
export const channelSearch = (state: IRootState) => state[prefix].search
export const scheduledMessages = (state: IRootState) => state[prefix].scheduledMessages
export const conversationSearch = (state: IRootState) => state[prefix].conversationSearch
export const channelSidePanel = (state: IRootState) => state[prefix].sidePanel
export const currentUserId = (state: IRootState) => state[prefix].currentUserId
export const splitPanes = (state: IRootState) => state[prefix].splitPanes

/**
 * The conversations open beside the main one, left to right. Compared by value, so a pane's
 * traffic — a new `splitPanes` array with the same ids — doesn't hand subscribers a new list.
 */
export const splitPaneChannelIds = createSelector(
  [splitPanes],
  (panes) => panes.map((pane) => pane.channelId),
  { memoizeOptions: { resultEqualityCheck: isEqual } },
)

/** The panes holding a conversation, rather than a person's homepage. */
export const splitPaneConversationIds = createSelector(
  [splitPaneChannelIds],
  (paneIds) => paneIds.filter((paneId) => !personOfPane(paneId)),
  { memoizeOptions: { resultEqualityCheck: isEqual } },
)

/**
 * Sidebar ordering: pinned first, then most recently active. Derived here rather
 * than stored so a live message reordering the list costs no reducer work.
 */
const byPinnedThenRecent = (a: IChannelListItem, b: IChannelListItem) => {
  if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1
  return (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt)
}

/** Named channels, for the "Channels" section of the sidebar. */
export const namedChannels = createSelector([channelsList], (channels) =>
  channels.filter((channel) => channel.kind === ChannelKind.Channel).sort(byPinnedThenRecent),
)

/** Each group's default glyph — see `assignGroupEmojiDefaults`. */
export const groupEmojiDefaults = createSelector([namedChannels], assignGroupEmojiDefaults, {
  memoizeOptions: { resultEqualityCheck: isEqual },
})

/** Each group's glyph, shared by the sidebar, the headers and the group's home. */
export const groupEmojis = createSelector([namedChannels, groupEmojiDefaults], resolveGroupEmojis, {
  memoizeOptions: { resultEqualityCheck: isEqual },
})

const EMPTY_TYPING: IChannelTypingUser[] = []

/**
 * Typing users in the open channel, excluding the viewer.
 *
 * Reads activeChannelId from state rather than taking it as an argument so it
 * composes with createStructuredSelector at connect time. The empty case returns
 * a shared constant — a fresh `[]` would be a new reference every call and defeat
 * the memoization.
 */
export const activeChannelTypingUsers = createSelector([getChannelsState], (state) => {
  if (!state.activeChannelId) return EMPTY_TYPING
  const users = state.typingUsers[state.activeChannelId]
  if (!users || users.length === 0) return EMPTY_TYPING
  return users.filter((user) => user.userId !== state.currentUserId)
})

/**
 * Arc's live reasoning, keyed by the message that summoned it. The reasoning panel
 * renders the trace and the timeline reads `isArcWorking` to say Arc is replying.
 */
export const channelArcProgress = (state: IRootState) => getChannelsState(state).arcProgress

const EMPTY_MENTION_INVITE: Array<{ id: string; name: string }> = []

/**
 * People the last sent message @-mentioned who still can't see it, for the offer to
 * add them. Filtered against the live roster rather than trusted as stored, so the
 * offer disappears once they're members — whether this prompt added them or someone
 * else did. Scoped to the open channel so it can't surface over a different one.
 */
export const unreachableMentionedUsers = createSelector(
  [(state: IRootState) => getChannelsState(state).mentionInvite, activeChannelId, activeChannel],
  (invite, channelId, channel) => {
    if (!invite || !channelId || invite.channelId !== channelId) return EMPTY_MENTION_INVITE
    const memberIds = new Set(channel?.members.map((member) => member.userId) ?? [])
    const users = invite.users.filter((user) => !memberIds.has(user.id))
    return users.length > 0 ? users : EMPTY_MENTION_INVITE
  },
)

const byRecentActivity = (
  a: Pick<IConversationGroup, 'lastMessageAt'>,
  b: Pick<IConversationGroup, 'lastMessageAt'>,
) => (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? '')

/** Sidebar conversation groups — one row per participant set with named conversations. */
export const conversationGroups = createSelector(
  [(state: IRootState) => getChannelsState(state).conversationGroups.items],
  (groups) => [...groups].sort(byRecentActivity),
)

export const isLoadingConversationGroups = (state: IRootState) =>
  getChannelsState(state).conversationGroups.isLoading

/**
 * Conversations inside each expanded group, keyed by `dmKey`.
 *
 * Handed to the sidebar whole rather than per-group: the component renders every
 * open group, and a selector taking a key would need one instance per group to
 * stay memoized.
 */
export const conversationsByGroup = (state: IRootState) =>
  getChannelsState(state).conversationsByGroup

/** Automations with scheduled runs — the sidebar's Scheduled runs section. */
export const scheduledRunGroups = (state: IRootState) =>
  getChannelsState(state).scheduledRunGroups.items

/** The conversation behind a `?share=` link — drives the landing modal. */
/**
 * The Conversations section's rows, already ordered by the server — unread
 * first, then recency, pinned above both.
 *
 * Deliberately not re-sorted here: the window the server returns is "everything
 * unread, or the ten most recent, whichever is larger", and a client-side sort
 * on a subset would reorder rows the cut was made against.
 */
export const recentConversations = (state: IRootState) =>
  getChannelsState(state).recentConversations

/**
 * What every unread surface is derived from, as one indicator.
 *
 * `recentConversations` rather than `channels` + `conversationGroups`, which is
 * the whole fix for the badge that stayed lit with nothing to click. Those two
 * slices carry group *rollups* the client maintains by delta — raised whenever a
 * message arrives, lowered only when a loaded row supplies a prior to subtract —
 * so unread ratcheted upward and a reload was the only way down. They also, in
 * opposite directions, disagreed about what to count: `conversationGroups` holds
 * no channel conversations at all, so unread in one was invisible here.
 *
 * This list has neither problem: every row is a conversation and its count comes
 * from the server. The Threads filter limits which rows load, so the server's
 * unfiltered `unreadConversations` covers the rest.
 */
export const totalUnreadIndicator = createSelector(
  [recentConversations],
  (recent): UnreadIndicator =>
    aggregateConversationsUnread(recent.items, recent.unreadConversations),
)

/**
 * The nav badge's number. Zero while something is unread means "show a dot" —
 * ask `totalUnreadIndicator` for which, rather than inferring it from this.
 */
export const totalUnreadCount = createSelector([totalUnreadIndicator], unreadIndicatorValue)

/**
 * Everything unread, for the surfaces outside the app: tab title, favicon, and the
 * OS app icon.
 *
 * True message volume rather than the dot/number-collapsed total: unlike the nav
 * badge, there is nowhere out there to point at *where* to look, so the tab can
 * only say how much is waiting. The same count a push puts on the app icon.
 */
export const totalUnreadMessages = createSelector(
  [recentConversations],
  (recent) => recent.unreadTotal,
)

export const peopleDirectory = (state: IRootState) => getChannelsState(state).people

export const channelHome = (state: IRootState) => getChannelsState(state).channelHome
export const personHome = (state: IRootState) => getChannelsState(state).personHome
export const arcHome = (state: IRootState) => getChannelsState(state).arcHome

export const channelShareLanding = (state: IRootState) => getChannelsState(state).shareLanding

/** Conversations whose timeline is cached, most recently opened first. */
export const selectCachedChannelIds = (state: IRootState) => state[prefix].messageCacheOrder
